import React, { RefObject } from 'react';
import { Sparkles, Shield, Zap, Globe } from 'lucide-react';

interface SolutionOverviewSectionProps {
  visibleSections: Set<string>;
  solutionSectionRef: RefObject<HTMLElement>;
}

const SolutionOverviewSection: React.FC<SolutionOverviewSectionProps> = ({
  visibleSections,
  solutionSectionRef,
}) => {
  const features = [
    {
      icon: Sparkles,
      title: 'Comprehensive Management',
      description:
        'Covering all aspects of customized invite management from guest lists to RSVP tracking.',
    },
    {
      icon: Shield,
      title: 'Quality Delivery',
      description:
        'Maintaining the highest quality of delivery for clients and their guests throughout the process.',
    },
    {
      icon: Zap,
      title: 'Automated Solutions',
      description:
        'Streamlined processes that eliminate manual work and reduce the burden on event hosts.',
    },
    {
      icon: Globe,
      title: 'Professional Standards',
      description:
        'Setting a new standard for guest-host relationships with professional, reliable service.',
    },
  ];

  return (
    <section
      id="solution-overview"
      ref={solutionSectionRef}
      className="relative z-10 bg-black"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none z-0" />

      <div className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <div
            className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${
              visibleSections.has('solution-overview')
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 translate-y-10 scale-105'
            }`}
          >
            <h2 className="font-megant text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">OUR SOLUTION</h2>
            <p className="font-montserrat text-gray-300 text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              We focus on covering all aspects of customized invite management, including compiling
              and organizing guest lists, collecting all relevant contact details, circulating event
              invitations, gathering and reporting RSVPs, creating a personalized event website,
              offering a dedicated phone line and more.
            </p>
          </div>

          {/* Feature Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`bg-gradient-to-br from-gray-900/30 to-transparent rounded-lg border border-gray-800/50 p-4 sm:p-6 md:p-8 transition-all duration-500 hover:border-green-400/30 hover:shadow-lg hover:shadow-green-400/10 hover:-translate-y-2 ${
                    visibleSections.has('solution-overview')
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-10'
                  }`}
                  style={{
                    transitionDelay: visibleSections.has('solution-overview')
                      ? `${300 + index * 100}ms`
                      : '0ms',
                  }}
                >
                  {/* Icon */}
                  <div className="mb-3 sm:mb-4 flex items-center justify-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-green-400/20 to-green-600/20 flex items-center justify-center border border-green-400/30 group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-green-400" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-megant text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3 text-center">
                    {feature.title}
                  </h3>
                  <p className="font-montserrat text-gray-300 text-xs sm:text-sm md:text-base leading-relaxed text-center">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="relative z-10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            className={`h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 ${
              visibleSections.has('solution-overview')
                ? 'opacity-100 scale-x-100'
                : 'opacity-0 scale-x-0'
            }`}
          />
        </div>
      </div>
    </section>
  );
};

export default SolutionOverviewSection;

